/**
 * 端末証明書(R7e)の一覧・発行・失効・メール再送・操作履歴。
 *
 * **正はサーバー**(自前のプライベート CA)。この画面はフォームを送るだけで、
 * 鍵も p12 も作らない。認証時の検証は Caddy の mTLS → R7d の判定に `cert` として入る。
 *
 * 秘密の扱い(ここが一番大事):
 *   - `p12_password` は**発行の応答にだけ**入る。state に置くだけで、
 *     `localStorage` にも URL にも入れない。ダイアログを閉じたら state から捨てる。
 *   - **パスワードはメールに入れない。** メールにはダウンロード URL だけが入るので、
 *     パスワードはこの画面で控えて別の手段で本人に渡す(画面にもそう書く)。
 *   - ダウンロード URL は公開ページ(`https://<host>/dc#<token>`)。トークンは
 *     **fragment** なのでアクセスログに残らない。**管理画面からは叩かない**
 *     (叩くと本人の前に「ダウンロード済み」になる)。文字として出してコピーさせるだけ。
 */
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Drawer,
  Flex,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";

import type {
  DeviceCert,
  DeviceCertIssued,
  DeviceCertKpi,
  DeviceCertLang,
  DeviceCertMailed,
  DeviceCertOs,
  DeviceCertStatus,
} from "../../lib/api";
import { jst } from "../../lib/format";
import {
  useDeviceCertEvents,
  useDeviceCerts,
  useIssueDeviceCert,
  useMailDeviceCert,
  useRevokeDeviceCert,
  useUpdateDeviceCert,
} from "./deviceCertHooks";
import {
  DEVICE_CERT_COLUMN_LABEL,
  DEVICE_CERT_EXPIRING_DAYS,
  DEVICE_CERT_LANG_OPTIONS,
  DEVICE_CERT_OS_LABEL,
  DEVICE_CERT_OS_OPTIONS,
  DEVICE_CERT_STATUS_LABEL,
  DEVICE_CERT_STATUS_OPTIONS,
  deviceCertActionLabel,
  deviceCertStatusColor,
} from "./deviceCertOptions";
import { errorText, useIdpPerms } from "./hooks";
import { UserPicker } from "./UserPicker";

const { Text, Paragraph } = Typography;

const dash = (v: string | null | undefined): string => (v ? v : "—");

/** 期限まで何日か(切り上げ)。過ぎていれば負。 */
function daysUntil(iso: string): number {
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return Number.POSITIVE_INFINITY;
  return Math.ceil((at - Date.now()) / 86_400_000);
}

/** まだ生きているか(失効・期限切れには再送も失効もさせない)。 */
const live = (s: DeviceCertStatus): boolean => s !== "revoked" && s !== "expired";

// ---- KPI ------------------------------------------------------------------------

/** CA の残り日数。null(CA 無し・読めない)なら null。 */
function caDaysLeft(at: string | null | undefined): number | null {
  if (!at) return null;
  const end = Date.parse(at);
  if (Number.isNaN(end)) return null;
  return Math.floor((end - Date.now()) / 86_400_000);
}

/**
 * 一覧の上の 6 枚。**数はサーバーが数える**(画面では行から数え直さない。
 * 一覧は絞り込みで減るので、絞ると KPI が嘘になる)。
 *
 * 最後の 1 枚は**社内 CA 自体の期限**(fix1 の中 6)。ここを過ぎると
 * 発行が止まるだけでなく、配った証明書が全部一斉に効かなくなる。
 */
function Kpi({ kpi }: { kpi: DeviceCertKpi | undefined }) {
  const n = (v: number | undefined) => v ?? 0;
  const over = n(kpi?.limit) > 0 && n(kpi?.issued) >= n(kpi?.limit);
  const caLeft = caDaysLeft(kpi?.ca_not_after);
  return (
    <Card size="small" style={{ marginBottom: 12 }}>
      <Flex gap={32} wrap>
        <Statistic
          title="発行中"
          value={n(kpi?.issued)}
          suffix={`/ 上限 ${n(kpi?.limit)}`}
          valueStyle={over ? { color: "#cf1322" } : undefined}
        />
        <Statistic title="未ダウンロード" value={n(kpi?.unused)} />
        <Statistic
          title={`${DEVICE_CERT_EXPIRING_DAYS} 日以内に期限`}
          value={n(kpi?.expiring_30d)}
          valueStyle={n(kpi?.expiring_30d) ? { color: "#d46b08" } : undefined}
        />
        <Statistic
          title="入替リクエスト"
          value={n(kpi?.revoke_requests)}
          valueStyle={n(kpi?.revoke_requests) ? { color: "#d46b08" } : undefined}
        />
        <Statistic title="入替可能" value={n(kpi?.replaceable)} />
        <Statistic
          title="社内 CA の期限"
          value={kpi?.ca_not_after ? jst(kpi.ca_not_after) : "—"}
          valueStyle={
            caLeft !== null && caLeft < 30
              ? { color: caLeft < 1 ? "#cf1322" : "#d46b08", fontSize: 18 }
              : { fontSize: 18 }
          }
          suffix={caLeft !== null ? `(残り ${caLeft} 日)` : undefined}
        />
      </Flex>
    </Card>
  );
}

// ---- 発行 -----------------------------------------------------------------------

type IssueValues = {
  user_id?: string;
  os?: DeviceCertOs;
  note?: string;
  mail_subject_note?: string;
  send_mail?: boolean;
  lang?: DeviceCertLang;
};

function IssueModal({
  open,
  onClose,
  onIssued,
}: {
  open: boolean;
  onClose: () => void;
  onIssued: (res: DeviceCertIssued) => void;
}) {
  const [form] = Form.useForm<IssueValues>();
  const issue = useIssueDeviceCert();
  const [sendMail, setSendMail] = useState(true);

  /**
   * 初期化は **`open` が false → true の瞬間だけ**。
   * 後から届く問い合わせの結果を依存に入れると、入力途中が消える(2026-09-28 の型)。
   */
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setSendMail(true);
  }, [open, form]);

  return (
    <Modal
      open={open}
      title="端末証明書を発行する"
      width={640}
      onCancel={onClose}
      onOk={() => void form.submit()}
      okText="発行する"
      cancelText="やめる"
      confirmLoading={issue.isPending}
      destroyOnClose
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="p12 のパスワードは発行の直後にこの画面で 1 回だけ出る"
        description="メールに入るのはダウンロード URL だけ。パスワードはこの画面で控えて、本人へ別の手段(口頭・電話など)で渡す。閉じたあとは誰も読めない。"
      />
      <Form
        form={form}
        layout="vertical"
        initialValues={{ os: "macos", send_mail: true, lang: "ja" }}
        onFinish={(v) =>
          issue.mutate(
            {
              user_id: v.user_id!,
              os: v.os ?? "macos",
              note: v.note ?? "",
              mail_subject_note: v.mail_subject_note ?? "",
              send_mail: v.send_mail ?? false,
              lang: v.lang ?? "ja",
            },
            {
              onSuccess: (res) => {
                onClose();
                onIssued(res);
                // 渡したら **mutation の手元からは消す**(自己レビュー 中 2)。
                // `issue.data` に p12 のパスワードが残ったままになるのを防ぐ
                issue.reset();
              },
            },
          )
        }
      >
        <Form.Item
          name="user_id"
          label="ユーザー"
          rules={[{ required: true, message: "ユーザーを選ぶ" }]}
        >
          <UserPicker />
        </Form.Item>
        <Form.Item name="os" label="OS" rules={[{ required: true, message: "OS を選ぶ" }]}>
          <Select options={DEVICE_CERT_OS_OPTIONS} style={{ width: 200 }} />
        </Form.Item>
        <Form.Item name="note" label="備考" extra="端末の名前・用途など。一覧に出る。">
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item
          name="mail_subject_note"
          label="メール件名メモ"
          extra="インストールメールの件名に差し込む({subject_note})。空でもよい。"
        >
          {/* API は 100 文字まで(DEVICE_CERT_SUBJECT_NOTE_MAX)。画面で止める */}
          <Input maxLength={100} />
        </Form.Item>
        <Form.Item name="send_mail" label="メールを送る">
          <Radio.Group onChange={(e) => setSendMail(Boolean(e.target.value))}>
            <Radio value={true}>送る</Radio>
            <Radio value={false}>送らない(URL を自分で渡す)</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item name="lang" label="メールの言語">
          <Select options={DEVICE_CERT_LANG_OPTIONS} style={{ width: 160 }} disabled={!sendMail} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

/**
 * 発行の結果。**パスワードはここだけ**。
 *
 * `afterClose` で state を捨てる(閉じるアニメーションの途中で空になると
 * 一瞬ちらつくので、閉じ終わってから捨てる)。
 */
function IssuedModal({
  issued,
  open,
  onClose,
  afterClose,
}: {
  issued: DeviceCertIssued | null;
  open: boolean;
  onClose: () => void;
  afterClose: () => void;
}) {
  return (
    <Modal
      open={open}
      title="発行した(パスワードはこの1回だけ)"
      width={640}
      onCancel={onClose}
      onOk={onClose}
      afterClose={afterClose}
      okText="閉じた(控えた)"
      cancelButtonProps={{ style: { display: "none" } }}
      maskClosable={false}
      // **閉じたら DOM から消す。** これが無いと antd は閉じた後もモーダルの中身を
      // 残すので、「二度と表示できない」と書いてある p12 のパスワードが**画面の
      // DOM に residual として残る**(開発者ツールから読める)。`afterClose` の
      // state 破棄だけでは、閉じるアニメーションの後も要素自体は残る
      destroyOnClose
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="この画面を閉じると p12 のパスワードは二度と表示できない"
        description="控えてから閉じる。忘れたときは失効させて発行し直す(再表示はできない)。"
      />
      <Descriptions size="small" column={1} bordered>
        <Descriptions.Item label="ユーザー">{issued?.item.username ?? "—"}</Descriptions.Item>
        <Descriptions.Item label="OS">
          {issued ? DEVICE_CERT_OS_LABEL[issued.item.os] : "—"}
        </Descriptions.Item>
        <Descriptions.Item label="p12 のパスワード">
          <Paragraph
            data-testid="p12-password"
            copyable={{ text: issued?.p12_password ?? "" }}
            style={{ margin: 0, wordBreak: "break-all" }}
          >
            <Text code>{issued?.p12_password ?? ""}</Text>
          </Paragraph>
        </Descriptions.Item>
        <Descriptions.Item label="ダウンロード URL">
          {/*
            公開 URL。**管理画面からは開かない**(開くと本人の前に「ダウンロード済み」に
            なる)。リンクにせず、文字としてコピーさせる。
          */}
          <Paragraph
            copyable={{ text: issued?.download_url ?? "" }}
            style={{ margin: 0, wordBreak: "break-all" }}
          >
            <Text code>{issued?.download_url ?? ""}</Text>
          </Paragraph>
        </Descriptions.Item>
        <Descriptions.Item label="リンクの有効期限">
          {dash(jst(issued?.bundle_expires_at))}
        </Descriptions.Item>
        <Descriptions.Item label="メール">
          {issued?.mail_sent ? (
            <Text>送った(パスワードは入っていない)</Text>
          ) : (
            <Text type="warning">送っていない。URL を自分で渡す</Text>
          )}
        </Descriptions.Item>
      </Descriptions>
    </Modal>
  );
}

// ---- 失効 -----------------------------------------------------------------------

function RevokeModal({ cert, onClose }: { cert: DeviceCert | null; onClose: () => void }) {
  const [form] = Form.useForm<{ reason?: string }>();
  const revoke = useRevokeDeviceCert();

  useEffect(() => {
    if (cert) form.resetFields();
  }, [cert, form]);

  return (
    <Modal
      open={!!cert}
      title={cert ? `${cert.username} の端末証明書を失効させる` : "失効"}
      onCancel={onClose}
      onOk={() => void form.submit()}
      okText="失効させる"
      okButtonProps={{ danger: true }}
      cancelText="やめる"
      confirmLoading={revoke.isPending}
      destroyOnClose
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="元に戻せない"
        description="失効させると、この証明書での認証はすぐに通らなくなる。使い続けるなら新しく発行し直す(パスワードも新しくなる)。"
      />
      <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}>
        <Descriptions.Item label="OS">
          {cert ? DEVICE_CERT_OS_LABEL[cert.os] : "—"}
        </Descriptions.Item>
        <Descriptions.Item label="備考">{dash(cert?.note)}</Descriptions.Item>
        <Descriptions.Item label="有効期限">{dash(jst(cert?.not_after))}</Descriptions.Item>
      </Descriptions>
      <Form
        form={form}
        layout="vertical"
        onFinish={(v) =>
          revoke.mutate({ id: cert!.id, reason: v.reason ?? "" }, { onSuccess: onClose })
        }
      >
        <Form.Item
          name="reason"
          label="理由(履歴と監査に残る)"
          rules={[{ required: true, message: "理由を書く" }]}
        >
          {/* API は 200 文字まで(REASON_MAX)。画面で止めて 400 を出さない */}
          <Input.TextArea rows={3} maxLength={200} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ---- メール再送 ------------------------------------------------------------------

function MailModal({ cert, onClose }: { cert: DeviceCert | null; onClose: () => void }) {
  const [form] = Form.useForm<{ lang?: DeviceCertLang }>();
  const mail = useMailDeviceCert();
  const [result, setResult] = useState<DeviceCertMailed | null>(null);

  useEffect(() => {
    if (cert) {
      form.resetFields();
      setResult(null);
    }
  }, [cert, form]);

  return (
    <Modal
      open={!!cert}
      title={cert ? `${cert.username} にインストールメールを送り直す` : "メール再送"}
      width={600}
      onCancel={onClose}
      onOk={result ? onClose : () => void form.submit()}
      okText={result ? "閉じる" : "送り直す"}
      cancelButtonProps={result ? { style: { display: "none" } } : undefined}
      cancelText="やめる"
      confirmLoading={mail.isPending}
      destroyOnClose
      afterClose={() => setResult(null)}
    >
      {result ? (
        <>
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="新しいダウンロード URL">
              <Paragraph
                copyable={{ text: result.download_url }}
                style={{ margin: 0, wordBreak: "break-all" }}
              >
                <Text code>{result.download_url}</Text>
              </Paragraph>
            </Descriptions.Item>
            <Descriptions.Item label="リンクの有効期限">
              {dash(jst(result.bundle_expires_at))}
            </Descriptions.Item>
            <Descriptions.Item label="メール">
              {result.mail_sent ? "送った" : "送れなかった。URL を自分で渡す"}
            </Descriptions.Item>
          </Descriptions>
          <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
            前のリンクはもう使えない。p12 のパスワードは変わっていない(再表示もできない)。
          </Paragraph>
        </>
      ) : (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="送り直すと前のダウンロードリンクは無効になる"
            description="メールに入るのは URL だけ。p12 のパスワードは入らず、再表示もできない(分からなくなったら失効させて発行し直す)。"
          />
          <Form
            form={form}
            layout="vertical"
            initialValues={{ lang: "ja" }}
            onFinish={(v) =>
              mail.mutate(
                { id: cert!.id, lang: v.lang ?? "ja" },
                {
                  onSuccess: (res) => {
                    setResult(res);
                    mail.reset();   // 新しいダウンロード URL をキャッシュに残さない
                  },
                },
              )
            }
          >
            <Form.Item name="lang" label="メールの言語">
              <Select options={DEVICE_CERT_LANG_OPTIONS} style={{ width: 160 }} />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
}

// ---- 操作履歴 --------------------------------------------------------------------

function EventsDrawer({ cert, onClose }: { cert: DeviceCert | null; onClose: () => void }) {
  const events = useDeviceCertEvents(cert?.id ?? null);
  return (
    <Drawer
      open={!!cert}
      width={640}
      onClose={onClose}
      destroyOnClose
      title={cert ? `${cert.username} の端末証明書の履歴` : "履歴"}
    >
      {events.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="履歴を取得できなかった"
          description={errorText(events.error)}
        />
      )}
      <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}>
        <Descriptions.Item label="状態">
          {cert && (
            <Tag color={deviceCertStatusColor(cert.status)}>
              {DEVICE_CERT_STATUS_LABEL[cert.status]}
            </Tag>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="シリアル">{dash(cert?.serial)}</Descriptions.Item>
        <Descriptions.Item label="発行">
          {dash(jst(cert?.created_at))}
          {cert?.issued_by ? `(${cert.issued_by})` : ""}
        </Descriptions.Item>
        <Descriptions.Item label="ダウンロード">
          {cert?.downloaded_at ? `${jst(cert.downloaded_at)}(${cert.download_count} 回)` : "まだ"}
        </Descriptions.Item>
        <Descriptions.Item label="失効">
          {cert?.revoked_at ? `${jst(cert.revoked_at)}(${cert.revoke_reason || "理由なし"})` : "—"}
        </Descriptions.Item>
      </Descriptions>
      <Table
        scroll={{ x: "max-content" }}
        size="small"
        rowKey={(r) => `${r.at}-${r.action}-${r.actor}`}
        loading={events.isFetching}
        pagination={false}
        dataSource={events.data?.items ?? []}
        locale={{ emptyText: "履歴がない" }}
        columns={[
          { title: "日時", dataIndex: "at", width: 180, render: (v: string) => jst(v) },
          {
            title: "操作",
            dataIndex: "action",
            width: 160,
            render: (v: string) => deviceCertActionLabel(v),
          },
          { title: "操作者", dataIndex: "actor", width: 160, render: (v: string) => dash(v) },
          { title: "詳細", width: 320, ellipsis: true, onCell: (row) => ({ title: String(row.detail ?? "") }), dataIndex: "detail", render: (v: string) => dash(v) },
        ]}
      />
    </Drawer>
  );
}

// ---- 一覧 -----------------------------------------------------------------------

export function DeviceCertsPage() {
  const { canRun } = useIdpPerms();
  const [qInput, setQInput] = useState("");
  const [filters, setFilters] = useState({ q: "", status: "", os: "" });
  const [issuing, setIssuing] = useState(false);
  const [issued, setIssued] = useState<DeviceCertIssued | null>(null);
  const [issuedOpen, setIssuedOpen] = useState(false);
  const [revoking, setRevoking] = useState<DeviceCert | null>(null);
  const [mailing, setMailing] = useState<DeviceCert | null>(null);
  const [history, setHistory] = useState<DeviceCert | null>(null);

  // viewer には出さない画面(ナビからも外してある)。開かれても問い合わせない(低 2)
  const query = useDeviceCerts(filters, canRun);
  const update = useUpdateDeviceCert();

  /** 備考・件名メモは表の中で直す(PATCH は差分だけ送る)。 */
  const editable = (row: DeviceCert, field: "note" | "mail_subject_note") =>
    canRun
      ? {
          text: row[field],
          // 件名メモだけ 100 文字(DEVICE_CERT_SUBJECT_NOTE_MAX)。備考は 200
          maxLength: field === "note" ? 200 : 100,
          tooltip: "クリックして直す",
          onChange: (v: string) => {
            if (v === row[field]) return;
            // 計算キーだと型が広がるので、送る鍵は素直に分ける
            update.mutate(
              field === "note" ? { id: row.id, note: v } : { id: row.id, mail_subject_note: v },
            );
          },
        }
      : false;

  const columns: ColumnsType<DeviceCert> = [
    {
      title: DEVICE_CERT_COLUMN_LABEL.username,
      dataIndex: "username",
      width: 200,
    },
    {
      title: DEVICE_CERT_COLUMN_LABEL.status,
      dataIndex: "status",
      width: 300,
      render: (v: DeviceCertStatus, row) => (
        <Space size={4} wrap>
          <Tag color={deviceCertStatusColor(v)}>{DEVICE_CERT_STATUS_LABEL[v]}</Tag>
          {row.replace_requested && <Tag color="gold">入替リクエスト</Tag>}
        </Space>
      ),
    },
    {
      title: DEVICE_CERT_COLUMN_LABEL.os,
      dataIndex: "os",
      width: 160,
      render: (v: DeviceCertOs) => DEVICE_CERT_OS_LABEL[v] ?? v,
    },
    {
      title: DEVICE_CERT_COLUMN_LABEL.note,
      ellipsis: true,
      onCell: (row) => ({ title: row.note || "—" }),
      dataIndex: "note",
      // 幅を指定しないと自由入力の備考が 1 文字ずつ折り返して縦書きに潰れる。
      // 幅の中で折り返させる。antd の ellipsis は付けない(td が nowrap +
      // overflow hidden になり、Text editable の鉛筆が切れて直せなくなる)
      width: 320,
      render: (v: string, row) => <Text editable={editable(row, "note")}>{v || "—"}</Text>,
    },
    {
      title: DEVICE_CERT_COLUMN_LABEL.mailSubjectNote,
      ellipsis: true,
      onCell: (row) => ({ title: row.mail_subject_note || "—" }),
      dataIndex: "mail_subject_note",
      // 備考と同じ(ellipsis なし。鉛筆を切らない)
      width: 320,
      render: (v: string, row) => (
        <Text editable={editable(row, "mail_subject_note")}>{v || "—"}</Text>
      ),
    },
    {
      title: DEVICE_CERT_COLUMN_LABEL.lastUsed,
      dataIndex: "last_used_at",
      width: 180,
      render: (v: string | null) => dash(jst(v)),
    },
    {
      title: DEVICE_CERT_COLUMN_LABEL.notAfter,
      dataIndex: "not_after",
      width: 320,
      render: (v: string, row) => {
        const days = daysUntil(v);
        return (
          <Space size={4} wrap>
            <span>{dash(jst(v))}</span>
            {live(row.status) && days >= 0 && days <= DEVICE_CERT_EXPIRING_DAYS && (
              <Tag color="orange">あと {days} 日</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: DEVICE_CERT_COLUMN_LABEL.actions,
      key: "actions",
      width: 270,
      render: (_: unknown, row) => (
        <Space size={0} wrap>
          {canRun && (
            <Button
              type="link"
              size="small"
              disabled={!live(row.status)}
              onClick={() => setMailing(row)}
            >
              メール再送
            </Button>
          )}
          {canRun && (
            <Button
              type="link"
              size="small"
              danger
              disabled={!live(row.status)}
              onClick={() => setRevoking(row)}
            >
              失効
            </Button>
          )}
          <Button type="link" size="small" onClick={() => setHistory(row)}>
            履歴
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Flex justify="space-between" align="center" wrap gap={8} style={{ marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          端末証明書
        </Typography.Title>
        {canRun && (
          <Button type="primary" onClick={() => setIssuing(true)}>
            証明書を発行
          </Button>
        )}
      </Flex>

      <Paragraph type="secondary">
        自前の CA で作った端末証明書。入れた端末からだけログインさせるために使う(判定は
        アクセスポリシーの条件式で書く)。
        <b>メールに入るのはダウンロード URL だけで、p12 のパスワードは入らない。</b>
        パスワードは発行のときに 1 回だけ画面に出る。
      </Paragraph>

      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="端末証明書を取得できなかった"
          description={errorText(query.error)}
        />
      )}

      <Kpi kpi={query.data?.kpi} />

      <Flex gap={8} wrap style={{ marginBottom: 12 }}>
        <Input.Search
          allowClear
          placeholder="ユーザー名・備考で探す"
          style={{ width: 320 }}
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          onSearch={(v) => setFilters((f) => ({ ...f, q: v }))}
        />
        <Select
          allowClear
          placeholder="状態"
          style={{ width: 180 }}
          value={filters.status || undefined}
          onChange={(v?: string) => setFilters((f) => ({ ...f, status: v ?? "" }))}
          options={DEVICE_CERT_STATUS_OPTIONS}
        />
        <Select
          allowClear
          placeholder="OS"
          style={{ width: 160 }}
          value={filters.os || undefined}
          onChange={(v?: string) => setFilters((f) => ({ ...f, os: v ?? "" }))}
          options={DEVICE_CERT_OS_OPTIONS}
        />
      </Flex>

      <Card size="small">
        <Table<DeviceCert>
          rowKey="id"
          size="small"
          loading={query.isFetching}
          columns={columns}
          dataSource={query.data?.items ?? []}
          locale={{ emptyText: "端末証明書が見つからない" }}
          scroll={{ x: 2070 }}
          pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `全 ${t} 件` }}
        />
      </Card>

      <IssueModal
        open={issuing}
        onClose={() => setIssuing(false)}
        onIssued={(res) => {
          setIssued(res);
          setIssuedOpen(true);
        }}
      />
      {/* パスワードは閉じ終わってから state ごと捨てる(どこにも残さない) */}
      <IssuedModal
        issued={issued}
        open={issuedOpen}
        onClose={() => setIssuedOpen(false)}
        afterClose={() => setIssued(null)}
      />
      <RevokeModal cert={revoking} onClose={() => setRevoking(null)} />
      <MailModal cert={mailing} onClose={() => setMailing(null)} />
      <EventsDrawer cert={history} onClose={() => setHistory(null)} />
    </div>
  );
}
